/**
 * QMS review queue — DR-2026-297.
 *
 * Design brief from the operator (s556): "the presentation and workflow must be
 * easy to see and understand and above all use ... some dashboards have been
 * built to dazzle rather than to be useable on a daily basis - we do not need
 * glitter. it needs to be to the point and functional."
 *
 * So, deliberately:
 *   - No charts, no KPI tiles, no animation. Counts are one line of text.
 *   - The default view is the daily question — undisposed items, decisions
 *     first — not the whole corpus.
 *   - The reason a finding is listed is IN the row. Scanning is the job; a
 *     detail hidden behind a click makes the list useless for triage.
 *   - Disposition is one click, inline. No modal, no confirm step.
 *   - Clicking the active disposition again CLEARS it. A mis-click must be
 *     undoable without help.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Container, Header, Table, Button, Label, Loader, Message, Input, Icon,
} from 'semantic-ui-react';
import { Helmet } from '@plone/volto/helpers';
import { getApiPath, fetchApi, getAuthHeaders } from '../../api';

const SEVERITY_COLOR = {
  critical: 'red', high: 'orange', medium: 'yellow', low: 'grey', info: 'grey',
};

// Plain words. `status_applicability` is what the detector calls it; nobody
// triaging at 9am should have to translate that.
const TYPE_LABEL = {
  status_applicability: 'Doc status',
  register_coverage: 'Not registered',
  doc_register_disagreement: 'Doc ≠ register',
  aiv_preventive_stale: 'AIV preventive',
  problem_report_open: 'Open report',
  // DR-2026-352: the failure-mode register's three finding types.
  failure_mode_new: 'New mode',
  failure_mode_missing: 'No mode',
  failure_mode_recurred_after_control: '5a recurrence',
};

const CLASS_HELP = {
  decision: 'Blocked on your judgement — no tool can settle these.',
  work: 'The answer is known; the work is not done.',
  informational: 'Recorded, no action expected.',
};

export default function ReviewQueue() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // DR-2026-352 D11: the failure-modes page links here filtered on a type. useLocation is
  // SSR-safe (the static router carries the URL); window.location would not be.
  const { search } = useLocation();
  const initial = React.useMemo(() => {
    const p = new URLSearchParams(search || '');
    const c = p.get('class');
    return { cls: c === 'all' ? null : (c || 'decision'), type: p.get('type') || null };
  }, [search]);
  const [cls, setCls] = useState(initial.cls);
  const [type, setType] = useState(initial.type);
  const [showDisposed, setShowDisposed] = useState(false);
  const [busy, setBusy] = useState({});
  const [deferring, setDeferring] = useState(null);
  const [deferDate, setDeferDate] = useState('');

  const load = useCallback(() => {
    const p = new URLSearchParams();
    if (cls) p.set('class', cls);
    if (type) p.set('type', type);
    if (showDisposed) p.set('disposed', 'true');
    setLoading(true);
    fetchApi(`${getApiPath()}/@qms/review-queue?${p.toString()}`)
      .then((d) => { setData(d); setError(null); })
      .catch((e) => setError(e.message || String(e)))
      .finally(() => setLoading(false));
  }, [cls, type, showDisposed]);

  useEffect(load, [load]);

  const dispose = async (item, disposition, reviewAfter) => {
    setBusy((b) => ({ ...b, [item.id]: true }));
    try {
      const res = await fetch(`${getApiPath()}/@qms/review-queue/${item.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ disposition, review_after: reviewAfter || null }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `HTTP ${res.status}`);
      }
      setDeferring(null);
      setDeferDate('');
      load();
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setBusy((b) => ({ ...b, [item.id]: false }));
    }
  };

  const counts = data?.counts || [];
  const needs = (c) => counts.find((x) => x.actionability === c)?.needs_review ?? 0;
  const items = data?.items || [];

  return (
    <Container className="view-wrapper" style={{ paddingTop: '1rem' }}>
      <Helmet title="QMS review queue" />

      <Header as="h1" style={{ marginBottom: '0.25rem' }}>Review queue</Header>

      {/* One line of counts. Not tiles — a tile per number is decoration that
          costs a screenful and answers nothing a sentence cannot. */}
      <p style={{ color: '#555', marginBottom: '0.75rem' }}>
        <strong>{needs('decision')}</strong> need a decision
        {' · '}<strong>{needs('work')}</strong> queued work
        {data?.overdue_deferrals ? (
          <>
            {' · '}
            <strong style={{ color: '#a33' }}>
              {data.overdue_deferrals} deferral{data.overdue_deferrals === 1 ? '' : 's'} past due
            </strong>
          </>
        ) : null}
      </p>

      <div style={{ marginBottom: '0.75rem' }}>
        {['decision', 'work', 'informational'].map((c) => (
          <Button
            key={c} size="tiny" basic={cls !== c} primary={cls === c}
            onClick={() => setCls(cls === c ? null : c)}
            title={CLASS_HELP[c]}
          >
            {c}
          </Button>
        ))}
        <Button size="tiny" basic={!!cls} onClick={() => setCls(null)}>all</Button>
        <Button
          size="tiny" basic={!showDisposed}
          style={{ marginLeft: '1rem' }}
          onClick={() => setShowDisposed((s) => !s)}
        >
          {showDisposed ? 'hiding nothing' : 'show reviewed'}
        </Button>
        {type ? (
          <Button size="tiny" onClick={() => setType(null)}>
            <Icon name="close" />{TYPE_LABEL[type] || type}
          </Button>
        ) : null}
      </div>

      {cls ? <p style={{ color: '#777', marginTop: '-0.5rem' }}>{CLASS_HELP[cls]}</p> : null}

      {error ? <Message negative onDismiss={() => setError(null)}>{error}</Message> : null}
      {loading ? <Loader active inline="centered" /> : null}

      {/* `!error` is load-bearing. Without it a failed fetch rendered the red
          error AND a green "Nothing here" directly beneath it — contradictory,
          and the green one is the dangerous half: a reassuring empty state on a
          page that just failed to load anything is a false clean in the UI
          layer. Observed live during bring-up (the aclcheck_error 500). */}
      {!loading && !error && !items.length ? (
        <Message positive>
          Nothing here. {cls === 'decision'
            ? 'No finding is waiting on your judgement.'
            : 'No open findings in this view.'}
        </Message>
      ) : null}

      {items.length ? (
        <Table compact="very" size="small" basic="very">
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell width={2}>Subject</Table.HeaderCell>
              <Table.HeaderCell width={2}>Why</Table.HeaderCell>
              <Table.HeaderCell width={8}>Detail</Table.HeaderCell>
              {/* The FINDING's age, not the queue row's. The row age read 0d on
                  every line while details said "114 days old" — the wrong
                  quantity, scannably wrong. */}
              <Table.HeaderCell width={1}>Age</Table.HeaderCell>
              <Table.HeaderCell width={4}>Recommendation</Table.HeaderCell>
              <Table.HeaderCell width={3}>Disposition</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {items.map((it) => (
              <Table.Row key={it.id} disabled={!!busy[it.id]}>
                <Table.Cell>
                  <code>{it.subject}</code>
                  <div>
                    <Label size="mini" color={SEVERITY_COLOR[it.severity]} horizontal>
                      {it.severity}
                    </Label>
                  </div>
                </Table.Cell>
                <Table.Cell>
                  <a
                    href="#filter"
                    onClick={(e) => { e.preventDefault(); setType(it.finding_type); }}
                    title="Show only this kind"
                  >
                    {TYPE_LABEL[it.finding_type] || it.finding_type}
                  </a>
                </Table.Cell>
                {/* The reason it is listed, in full. This is what makes the row
                    triageable without a second click. */}
                <Table.Cell style={{ color: '#333' }}>{it.detail}</Table.Cell>
                <Table.Cell>
                  {it.finding_age_days != null ? `${it.finding_age_days}d` : '—'}
                </Table.Cell>
                {/* The recommendation and its BASIS. The basis is shown, not
                    hidden behind a tooltip: agreeing with reasoning you cannot
                    see is a rubber stamp, which is the opposite of what the
                    operator asked for. */}
                <Table.Cell>
                  {it.recommendation ? (
                    <>
                      <Label size="small" color={
                        it.recommendation === 'done' ? 'green'
                          : it.recommendation === 'dismissed' ? 'grey' : 'blue'
                      }>
                        {it.recommendation}
                      </Label>
                      <div style={{ fontSize: '0.85em', color: '#555', marginTop: '0.3rem' }}>
                        {it.recommendation_basis}
                      </div>
                    </>
                  ) : (
                    <span style={{ color: '#999' }}>
                      no recommendation — needs your judgement
                    </span>
                  )}
                </Table.Cell>
                <Table.Cell>
                  {deferring === it.id ? (
                    <Input
                      size="mini" type="date" value={deferDate}
                      onChange={(e, { value }) => setDeferDate(value)}
                      action={{
                        size: 'mini', content: 'set', disabled: !deferDate,
                        onClick: () => dispose(it, 'deferred', deferDate),
                      }}
                    />
                  ) : (
                    <Button.Group size="mini" basic>
                      {['accepted', 'dismissed', 'deferred'].map((d) => (
                        <Button
                          key={d}
                          positive={it.disposition === d && d === 'accepted'}
                          active={it.disposition === d}
                          onClick={() => (
                            it.disposition === d
                              ? dispose(it, null)          // click again = undo
                              : d === 'deferred'
                                ? setDeferring(it.id)
                                : dispose(it, d)
                          )}
                          title={it.disposition === d ? 'Click again to undo' : ''}
                        >
                          {d === 'accepted' ? 'accept' : d === 'dismissed' ? 'dismiss' : 'defer'}
                        </Button>
                      ))}
                    </Button.Group>
                  )}
                  {it.disposition ? (
                    <div style={{ fontSize: '0.8em', color: '#777', marginTop: '0.25rem' }}>
                      {it.disposition} by {it.disposition_by}
                      {it.review_after ? ` · review ${it.review_after.slice(0, 10)}` : ''}
                    </div>
                  ) : null}
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      ) : null}
    </Container>
  );
}
